import { clamp } from '../engine/mathUtils';
import type { DriveInput } from '../physics/RaycastVehicle';

export type InputSourceId = 'keyboard' | 'touch' | 'gyro';

export interface RawInput {
  steer: number;
  throttle: number;
  brake: number;
  handbrake: boolean;
}

const emptyInput = (): RawInput => ({ steer: 0, throttle: 0, brake: 0, handbrake: false });

/**
 * Sammelstelle fuer alle Eingabequellen. Tastatur, Touch-Knoepfe, Lenkrad und
 * Gyro schreiben in dieselben Felder; die Spielschleife liest nur `combined`.
 * Keine Events, keine Allokationen pro Frame.
 */
export class InputHub {
  readonly keyboard = emptyInput();
  readonly touch = emptyInput();
  readonly gyro = emptyInput();

  /** Wird nur benutzt, wenn der Gyro-Modus aktiv ist. */
  gyroEnabled = false;

  private readonly out: DriveInput = { steer: 0, throttle: 0, brake: 0, handbrake: false };

  // Einmalige Aktionen: von der UI gesetzt, von der Schleife konsumiert.
  private resetFlag = false;
  private cameraFlag = false;
  hornActive = false;

  get sources(): readonly RawInput[] {
    return this.gyroEnabled ? [this.keyboard, this.touch, this.gyro] : [this.keyboard, this.touch];
  }

  /** Staerkster Wert gewinnt - so stoeren sich Tastatur und Touch nicht. */
  get combined(): DriveInput {
    let steer = 0;
    let throttle = 0;
    let brake = 0;
    let handbrake = false;
    for (const s of this.sources) {
      if (Math.abs(s.steer) > Math.abs(steer)) steer = s.steer;
      if (s.throttle > throttle) throttle = s.throttle;
      if (s.brake > brake) brake = s.brake;
      handbrake = handbrake || s.handbrake;
    }
    this.out.steer = clamp(steer, -1, 1);
    this.out.throttle = clamp(throttle, 0, 1);
    this.out.brake = clamp(brake, 0, 1);
    this.out.handbrake = handbrake;
    return this.out;
  }

  requestReset(): void {
    this.resetFlag = true;
  }

  requestCameraChange(): void {
    this.cameraFlag = true;
  }

  consumeReset(): boolean {
    const v = this.resetFlag;
    this.resetFlag = false;
    return v;
  }

  consumeCameraChange(): boolean {
    const v = this.cameraFlag;
    this.cameraFlag = false;
    return v;
  }

  clearTouch(): void {
    this.touch.steer = 0;
    this.touch.throttle = 0;
    this.touch.brake = 0;
    this.touch.handbrake = false;
    this.hornActive = false;
  }

  /** Loescht alle gehaltenen Eingaben nach Tabwechsel, Rotation oder Pause. */
  clearTransient(): void {
    for (const source of [this.keyboard, this.touch, this.gyro]) {
      source.steer = 0;
      source.throttle = 0;
      source.brake = 0;
      source.handbrake = false;
    }
    this.hornActive = false;
    this.resetFlag = false;
    this.cameraFlag = false;
  }
}
